/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import CheckIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useConnectors } from '@atlassian/conversation-assistant-store/controllers/connectors-store';

import { RovoLogoForAppSource } from '../../../common/ui/RovoLogoForAppSource';
import { useAppOAuth } from '../chat-mode-menu/app-filter/oauth/useAppOAuth';

import { messages } from './messages';

const styles = cssMap({
	bannerWrapper: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.150'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		marginBlockEnd: token('space.150'),
		minHeight: '40px',
	},
	bannerWrapperMultiline: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.150'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		marginBlockEnd: token('space.150'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	contentContainer: {
		flex: 1,
		overflow: 'hidden',
	},
	topRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: token('space.100'),
	},
	buttonsRow: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: token('space.100'),
	},
	buttonContent: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		fontWeight: token('font.weight.regular'),
	},
	buttonsContainer: {
		flexShrink: 0,
	},
	rowLabel: {
		// Allow the name to shrink/truncate so the action stays pinned to the right.
		minWidth: 0,
	},
	// Bordered card that holds the connector rows in the in-thread 3P variant.
	rowsCard: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		overflow: 'hidden',
	},
	row: {
		// Grid (not flex) so the single child still stretches full-width by default
		// (justify-items: stretch), keeping the button/chip flush right via its own
		// spread="space-between" — a flex child wouldn't stretch on the main axis.
		display: 'grid',
		alignItems: 'center',
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
		// 3rem = Button's 32px height + this row's own 2×space.100 padding, so the
		// row stays this tall even when the shorter Connected chip is showing.
		minHeight: '3rem',
	},
	rowDivider: {
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
	},
	connectedChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		paddingInline: token('space.100'),
		paddingBlock: token('space.025'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.success'),
		backgroundColor: token('color.background.success'),
		color: token('color.text.success'),
	},
});

/** Intl.ListFormat is ES2020; type assertion for TS libs that don't declare it. */
const ListFormatConstructor = (
	Intl as typeof Intl & {
		ListFormat: new (
			locale: string,
			opts: { style: string; type: string },
		) => {
			format: (list: string[]) => string;
			formatToParts: (list: string[]) => Array<{ type: 'element' | 'literal'; value: string }>;
		};
	}
).ListFormat;

/**
 * Formats app names for display using proper i18n list formatting.
 * e.g. conjunction → "A and B", disjunction → "A or B" (varies by locale)
 */
function formatAppList(
	names: string[],
	locale: string,
	type: 'conjunction' | 'disjunction',
): string {
	if (names.length === 1) return names[0];

	const formatter = new ListFormatConstructor(locale, {
		style: 'long',
		type,
	});
	return formatter.format(names);
}

/**
 * Like {@link formatAppList} but renders each app name in bold, preserving the
 * locale-correct separators (", ", " or ", …) between them.
 */
function formatAppListNodes(
	names: string[],
	locale: string,
	type: 'conjunction' | 'disjunction',
): React.ReactNode {
	if (names.length === 1) {
		return <Text as="strong">{names[0]}</Text>;
	}

	const formatter = new ListFormatConstructor(locale, {
		style: 'long',
		type,
	});
	return formatter.formatToParts(names).map((part, index) =>
		part.type === 'element' ? (
			<Text key={index} as="strong">
				{part.value}
			</Text>
		) : (
			<React.Fragment key={index}>{part.value}</React.Fragment>
		),
	);
}

export interface UnconnectedApp {
	source: string;
	friendlyName: string;
	iconUrl: string;
	outboundAuthUrl: string;
}

interface ConnectAppButtonProps {
	app: UnconnectedApp;
	connectLabel: string;
	onConnectSuccess: (appSource: string) => void;
}

const ConnectAppButton = ({ app, connectLabel, onConnectSuccess }: ConnectAppButtonProps) => {
	const handleConnectSuccess = useCallback(() => {
		onConnectSuccess(app.source);
	}, [app.source, onConnectSuccess]);

	const { onAuthorize } = useAppOAuth({
		friendlyName: app.friendlyName,
		appSource: app.source,
		outboundAuthUrl: app.outboundAuthUrl,
		onCompletedAction: handleConnectSuccess,
		analyticsAttributes: {
			source: 'connectionBanner',
		},
	});

	const icon = fg('rovo_chat_use_be_knowledge_icons') ? (
		<Image src={app.iconUrl} alt={app.friendlyName} width={20} height={20} />
	) : (
		<RovoLogoForAppSource appSource={app.source} rovoLogoProps={{ size: 'xsmall' }} />
	);

	return (
		<Button onClick={onAuthorize}>
			<Box xcss={styles.buttonContent}>
				{icon}
				{connectLabel}
			</Box>
		</Button>
	);
};

export interface ConnectRowProps {
	app: UnconnectedApp;
	/** Short visible label ("Connect") — the app name is shown as the row label. */
	connectLabel: string;
	/** Accessible name for the connect button, including the app name. */
	connectAccessibleLabel: string;
	connectedLabel: string;
	onConnectSuccess: (appSource: string) => void;
}

/**
 * A single connector row for the 3P "Connected" variant: the app icon and name
 * on the left, and on the right either the Connect button or — once the user has
 * connected it — a non-interactive "Connected" chip. Connection state is read
 * live from the connectors store (`isUserOAuthed`), which the shared OAuth flow
 * updates, so the row reflects it without the banner tracking its own state.
 */
const ConnectRow = ({
	app,
	connectLabel,
	connectAccessibleLabel,
	connectedLabel,
	onConnectSuccess,
}: ConnectRowProps) => {
	const [connectors] = useConnectors();

	const handleConnectSuccess = useCallback(() => {
		onConnectSuccess(app.source);
	}, [app.source, onConnectSuccess]);

	const { onAuthorize } = useAppOAuth({
		friendlyName: app.friendlyName,
		appSource: app.source,
		outboundAuthUrl: app.outboundAuthUrl,
		onCompletedAction: handleConnectSuccess,
		analyticsAttributes: {
			source: 'connectionBanner',
		},
	});

	const icon = fg('rovo_chat_use_be_knowledge_icons') ? (
		<Image src={app.iconUrl} alt="" width={24} height={24} />
	) : (
		<RovoLogoForAppSource appSource={app.source} rovoLogoProps={{ size: 'small' }} />
	);

	return (
		<Inline space="space.100" alignBlock="center" spread="space-between">
			<Inline space="space.100" alignBlock="center" xcss={styles.rowLabel}>
				{icon}
				<Text>{app.friendlyName}</Text>
			</Inline>
			{connectors[app.source]?.isUserOAuthed ? (
				<Box xcss={styles.connectedChip}>
					<CheckIcon size="small" label="" color={token('color.icon.success')} />
					<Text size="small" color="inherit">
						{connectedLabel}
					</Text>
				</Box>
			) : (
				<Button onClick={onAuthorize} aria-label={connectAccessibleLabel}>
					{connectLabel}
				</Button>
			)}
		</Inline>
	);
};

interface AppConnectionBannerProps {
	unconnectedApps: UnconnectedApp[];
	onDismiss: () => void;
	/** Callback when an app is successfully connected via OAuth */
	onConnect: (appSource: string) => void;
	/** When true, connect buttons are always on the next line */
	forceMultiline?: boolean;
	/**
	 * When true, a connected row is kept and shows a non-interactive "Connected"
	 * state instead of being removed (3P suggested-prompts experiment).
	 */
	showConnectedState?: boolean;
}

export const AppConnectionBanner = ({
	unconnectedApps,
	onDismiss,
	onConnect,
	forceMultiline = false,
	showConnectedState = false,
}: AppConnectionBannerProps): React.JSX.Element | null => {
	const { formatMessage, locale } = useIntl();

	if (unconnectedApps.length === 0) {
		return null;
	}

	const appNames = unconnectedApps.map((app) => app.friendlyName);
	// The 3P connected-state variant uses an invitational intro ("Connect X or Y…"),
	// so the app names read as alternatives (disjunction) rather than a result set.
	const bannerMessage = showConnectedState
		? formatMessage(messages.connectPromptIntro, {
				appList: formatAppListNodes(appNames, locale, 'disjunction'),
			})
		: formatMessage(messages.resultsAreFromApps, {
				appList: formatAppList(appNames, locale, 'conjunction'),
			});
	// 3P suggested-prompts variant: rendered inline in the chat thread
	// (InThreadAppConnectionPrompt), so it reads as assistant message content — an
	// invitational intro followed by per-connector rows, with no banner surface or
	// dismiss chrome.
	if (showConnectedState) {
		return (
			<Stack space="space.150">
				<Text size="medium">{bannerMessage}</Text>
				<Box xcss={styles.rowsCard}>
					{unconnectedApps.map((app, index) => (
						<Box
							key={app.source}
							xcss={cx(styles.row, index < unconnectedApps.length - 1 && styles.rowDivider)}
						>
							<ConnectRow
								app={app}
								connectLabel={formatMessage(messages.connect)}
								connectAccessibleLabel={formatMessage(messages.connectAppName, {
									appName: app.friendlyName,
								})}
								connectedLabel={formatMessage(messages.connected)}
								onConnectSuccess={onConnect}
							/>
						</Box>
					))}
				</Box>
			</Stack>
		);
	}

	const isMultiline = forceMultiline || unconnectedApps.length > 2;

	const closeButton = (
		<IconButton
			spacing="compact"
			appearance="subtle"
			shape="circle"
			label={formatMessage(messages.dismissBannerLabel)}
			icon={() => <CrossIcon size="small" label="" color={token('color.text.subtlest')} />}
			onClick={onDismiss}
		/>
	);

	const connectButtons = unconnectedApps.map((app) => (
		<ConnectAppButton
			key={app.source}
			app={app}
			connectLabel={formatMessage(messages.connectAppName, { appName: app.friendlyName })}
			onConnectSuccess={onConnect}
		/>
	));

	if (isMultiline) {
		return (
			<Box xcss={styles.bannerWrapperMultiline}>
				<Box xcss={styles.topRow}>
					<Box xcss={styles.contentContainer}>
						<Text size="medium">{bannerMessage}</Text>
					</Box>
					{closeButton}
				</Box>
				<Box xcss={styles.buttonsRow}>{connectButtons}</Box>
			</Box>
		);
	}

	return (
		<Box xcss={styles.bannerWrapper}>
			<Inline space="space.150" alignBlock="center" spread="space-between">
				<Box xcss={styles.contentContainer}>
					<Text size="medium">{bannerMessage}</Text>
				</Box>
				<Inline space="space.100" alignBlock="center" xcss={styles.buttonsContainer}>
					{connectButtons}
					{closeButton}
				</Inline>
			</Inline>
		</Box>
	);
};
